import { FC } from 'react';
import { Link } from 'react-router-dom';
import { Card } from './Card';
import { Button } from './Button';
import { PricingPlan } from '@/types';
import { cn } from '@/utils/cn';

interface PricingCardProps {
  plan: PricingPlan;
  compact?: boolean;
}

export const PricingCard: FC<PricingCardProps> = ({ plan, compact = false }) => {
  // 根据套餐等级设置不同的样式
  const getPlanStyles = () => {
    switch (plan.id) {
      // Claude 拼车套餐样式
      case 'claude-carpool':
        return {
          cardClass: 'bg-gradient-to-br from-blue-50 via-white to-purple-50 border-2 border-primary-500 shadow-xl shadow-primary-100',
          badgeClass: 'bg-gradient-to-r from-primary-600 to-accent-500',
          priceClass: 'text-primary-600',
          buttonClass: 'bg-gradient-to-r from-primary-600 to-accent-500 hover:from-primary-700 hover:to-accent-600 text-white shadow-lg',
          highlight: false,
          badge: '🚗 拼车'
        };
      // Claude 独享账号样式
      case 'claude-exclusive':
        return {
          cardClass: 'bg-gradient-to-br from-amber-50 via-white to-orange-50 border-2 border-amber-500 shadow-2xl shadow-amber-100',
          badgeClass: 'bg-gradient-to-r from-amber-600 to-orange-600',
          priceClass: 'text-amber-600',
          buttonClass: 'bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-700 hover:to-orange-700 text-white shadow-xl',
          highlight: true,
          badge: '🏅 独享账号'
        };
      // Claude 流量包样式
      case 'claude-package':
        return {
          cardClass: 'bg-gradient-to-br from-green-50 via-emerald-50 to-teal-50 border-2 border-green-500 shadow-xl shadow-green-100',
          badgeClass: 'bg-gradient-to-r from-green-600 to-emerald-600',
          priceClass: 'text-green-700',
          buttonClass: 'bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white shadow-lg',
          highlight: false,
          badge: '🎟️ 流量包'
        };
      // GLM 官方独享套餐样式
      case 'glm-official-exclusive':
        return {
          cardClass: 'bg-gradient-to-br from-red-50 via-orange-50 to-yellow-50 border-2 border-red-500 shadow-xl shadow-red-200 hover:shadow-2xl hover:shadow-red-300 transition-all duration-300',
          badgeClass: 'bg-gradient-to-r from-red-600 to-orange-600',
          priceClass: 'text-red-700',
          buttonClass: 'bg-gradient-to-r from-red-600 to-orange-600 hover:from-red-700 hover:to-orange-700 text-white shadow-lg hover:shadow-xl',
          highlight: true,
          badge: '🏅 官方独享'
        };
      // GPT-5 官方Plus套餐样式
      case 'gpt5-plus':
        return {
          cardClass: 'bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50 border-2 border-emerald-500 shadow-xl shadow-emerald-200 hover:shadow-2xl hover:shadow-emerald-300 transition-all duration-300',
          badgeClass: 'bg-gradient-to-r from-emerald-600 to-teal-600',
          priceClass: 'text-emerald-700',
          buttonClass: 'bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white shadow-lg hover:shadow-xl',
          highlight: false,
          badge: '🔥 官方代充'
        };
      // GPT-5 官方Pro套餐样式
      case 'gpt5-pro':
        return {
          cardClass: 'bg-gradient-to-br from-violet-50 via-purple-50 to-fuchsia-50 border-2 border-violet-500 shadow-2xl shadow-violet-200 hover:shadow-3xl hover:shadow-violet-300 transition-all duration-300',
          badgeClass: 'bg-gradient-to-r from-violet-600 to-purple-600',
          priceClass: 'text-violet-700',
          buttonClass: 'bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700 text-white shadow-xl hover:shadow-2xl',
          highlight: true,
          badge: '🏆 专业代充'
        };
      default:
        return {
          cardClass: '',
          badgeClass: '',
          priceClass: '',
          buttonClass: '',
          highlight: false,
          badge: ''
        };
    }
  };

  let styles = getPlanStyles();

  // 如果售罄，覆盖样式为灰度
  if (plan.soldOut) {
    styles = {
      ...styles,
      cardClass: 'bg-gray-100 border-gray-300',
      badgeClass: 'bg-gray-500',
      priceClass: 'text-gray-400',
      buttonClass: 'bg-gray-400 text-white cursor-not-allowed',
      highlight: false,
      badge: '已售罄'
    };
  }

  // 量化数据提取
  const getQuantityData = () => {
    switch (plan.id) {
      // Claude 拼车量化数据
      case 'claude-carpool':
        return {
          dailyTokens: '60$(约6小时)',
          models: 'Claude全模型',
          contextWindow: '1M',
          concurrent: '拼车共享'
        };
      // Claude 独享账号量化数据
      case 'claude-exclusive':
        return {
          dailyTokens: '每日无限制',
          models: 'Claude全模型访问',
          contextWindow: '1M',
          concurrent: '5个终端'
        };
      // Claude 流量包量化数据
      case 'claude-package':
        return {
          totalQuota: '$500',
          models: 'Claude全模型',
          contextWindow: '1M',
          validDays: '90天(3个月)'
        };
      // GLM 官方独享量化数据
      case 'glm-official-exclusive':
        return {
          dailyTokens: '30$(约3小时)',
          models: 'GLM-5',
          contextWindow: '1M',
          concurrent: '5个终端'
        };
      // GPT-5 官方Plus量化数据
      case 'gpt5-plus':
        return {
          dailyTokens: '20$(官方额度)',
          models: 'GPT-5/o1',
          contextWindow: '1M',
          concurrent: '官方账号'
        };
      // GPT-5 官方Pro量化数据
      case 'gpt5-pro':
        return {
          dailyTokens: '200$(官方额度)',
          models: 'GPT-5/o1',
          contextWindow: '1',
          concurrent: '官方账号'
        };
      default:
        return {};
    }
  };

  const quantityData = getQuantityData();

  return (
    <Card className={cn(
      'relative transform transition-all duration-300 h-full flex flex-col',
      plan.soldOut ? 'opacity-60 hover:scale-100 grayscale' : 'hover:scale-105',
      styles.cardClass,
      styles.highlight && !plan.soldOut && 'ring-2 ring-primary-500 ring-offset-2'
    )}>
      {/* Sold Out Overlay */}
      {plan.soldOut && (
        <div className="absolute inset-0 bg-black/20 z-20 rounded-lg pointer-events-none" />
      )}

      {/* Plan Badge */}
      <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 z-10">
        <span className={cn(
          'text-white px-4 py-1.5 text-sm font-bold rounded-full shadow-lg',
          plan.soldOut ? 'bg-gray-500' : styles.badgeClass
        )}>
          {plan.badge || styles.badge}
        </span>
      </div>

      <div className="text-center pt-4 flex flex-col h-full">
        {/* Plan Name */}
        <h3 className={cn(
          'text-3xl font-bold mb-3',
          styles.priceClass
        )}>
          {plan.name}
        </h3>
        
        {/* Pricing */}
        <div className="mb-6">
          {plan.firstMonthPrice ? (
            // 双价格显示（首月价格 + 后续价格）
            <div>
              <div className="flex flex-col items-center space-y-2">
                <div className="flex items-baseline space-x-2">
                  <span className="text-gray-600 text-sm">首月</span>
                  <span className={cn(
                    'text-4xl font-bold',
                    styles.priceClass
                  )}>
                    ¥{plan.firstMonthPrice}
                  </span>
                </div>
                <div className="flex items-baseline space-x-2">
                  <span className="text-gray-600 text-sm">后续</span>
                  <span className={cn(
                    'text-3xl font-bold',
                    styles.priceClass
                  )}>
                    ¥{plan.price}
                  </span>
                  <span className="text-gray-600 text-base">/月</span>
                </div>
              </div>
            </div>
          ) : (
            // 单价格显示
            <div className="flex items-baseline justify-center space-x-2">
              {plan.originalPrice && (
                <span className="text-lg text-gray-400 line-through">
                  ¥{plan.originalPrice}
                </span>
              )}
              <span className={cn(
                'text-5xl font-bold',
                styles.priceClass
              )}>
                ¥{plan.price}
              </span>
              <span className="text-gray-600 text-lg">{plan.isPackage ? '/3个月' : '/月'}</span>
            </div>
          )}

          {plan.isPackage && (
            <div className="mt-2 text-sm text-gray-500">一次性支付 · {plan.validDays}天有效</div>
          )}

          {plan.discount && (
            <div className="mt-3">
              <span className="bg-gradient-to-r from-warning-500 to-red-500 text-white px-3 py-1.5 text-sm font-bold rounded-full animate-pulse">
                🔥 限时优惠 省¥{plan.discount}
              </span>
            </div>
          )}
        </div>

        {/* Key Metrics - 重点量化数据 */}
        <div className="bg-white/70 backdrop-blur rounded-xl p-1 mb-6 space-y-4 mx-2">
          <div className="flex justify-between items-center border-b border-gray-200 pb-3">
            <span className="text-base text-gray-600">{plan.isPackage ? '💎 总流量额度' : '📊 每日限额'}</span>
            <span className={cn('font-bold text-sm', styles.priceClass)}>
              {plan.isPackage ? quantityData.totalQuota : quantityData.dailyTokens}
            </span>
          </div>
          <div className="flex justify-between items-center border-b border-gray-200 pb-3">
            <span className="text-base text-gray-600">🤖 可用模型</span>
            <span className={cn('font-bold text-sm', styles.priceClass)}>
              {quantityData.models}
            </span>
          </div>
          <div className="flex justify-between items-center border-b border-gray-200 pb-3">
            <span className="text-base text-gray-600">📝 上下文窗口</span>
            <span className={cn('font-bold text-base', styles.priceClass)}>
              {quantityData.contextWindow}
            </span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-base text-gray-600">{plan.isPackage ? '⏰ 有效期' : '💻 并发终端'}</span>
            <span className={cn('font-bold text-base', styles.priceClass)}>
              {plan.isPackage ? quantityData.validDays : quantityData.concurrent}
            </span>
          </div>
        </div>

        {/* Description */}
        <p className="text-gray-600 text-sm mb-6 px-2">{plan.description}</p>

        {/* Features - 可伸缩区域 */}
        <div className="flex-1">
          {!compact && (
            <div className="text-left mb-6">
              <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
                <span className="text-green-500 mr-2">✨</span>
                包含功能
              </h4>
              <ul className="space-y-2">
                {plan.features.slice(0, 5).map((feature, index) => (
                  <li key={index} className="flex items-start space-x-2">
                    <span className="text-green-500 mt-0.5 flex-shrink-0">✓</span>
                    <span className="text-sm text-gray-700">{feature}</span>
                  </li>
                ))}
                {plan.features.length > 5 && (
                  <li className="text-sm text-gray-500 pl-6">
                    还有 {plan.features.length - 5} 项高级功能...
                  </li>
                )}
              </ul>
            </div>
          )}
        </div>

        {/* 底部固定区域 */}
        <div className="mt-auto">
          {/* CTA Button */}
          {plan.soldOut ? (
            <button
              disabled
              className={cn(
                'w-full font-bold py-3 text-lg mb-4 rounded-lg text-center cursor-not-allowed bg-gray-400 text-white'
              )}
            >
              已售罄
            </button>
          ) : plan.externalUrl ? (
            <a
              href={plan.externalUrl}
              target="_blank"
              rel="noopener noreferrer"
              className={cn(
                'block w-full font-bold py-3 text-lg transform transition-all duration-300 hover:scale-105 mb-4 rounded-lg text-center',
                styles.buttonClass
              )}
            >
              前往官方购买 →
            </a>
          ) : (
            <Button
              as={Link}
              to="/contact"
              variant="custom"
              className={cn(
                'w-full font-bold py-3 text-lg transform transition-all duration-300 hover:scale-105 mb-4',
                styles.buttonClass
              )}
            >
              选择 {plan.name}
            </Button>
          )}

          {/* Support Info */}
          <div className="pt-4 border-t border-gray-200">
            <p className="text-xs text-gray-600">
              {/* <span className="font-semibold">技术支持：</span> */}
              {/* <span className={cn('font-bold ml-1', styles.priceClass)}>
                {plan.support}
              </span> */}
            </p>
          </div>
        </div>
      </div>
    </Card>
  );
};